import React, { useState } from "react";
import { Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { salesProposalConvertEndpoint } from "../../../api/endpoints";
import InlineBrandSelector from "../../videoProduction/components/InlineBrandSelector";
import { ProposalDetail } from "../../../components/ProposalFlow/types";

// Convert a proposal into a live (confirmed) order: pick/create the brand,
// confirm. No product is collected — the order and its projects convert with
// whatever (possibly no) product the wizard attached; products get assigned
// later through the normal project workflow. Internal-only — the detail page
// that renders this is already behind the internal router.
interface ConvertProposalModalProps {
    proposal: ProposalDetail;
    open: boolean;
    onClose: () => void;
    onConverted: (orderId: number) => void;
}

const noopSetFieldValue = () => undefined;

export function ConvertProposalModal({
    proposal,
    open,
    onClose,
    onConverted,
}: ConvertProposalModalProps) {
    const [selectedBrand, setSelectedBrand] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);

    const leadBrandName = proposal.proposal_meta?.brand_name || null;
    const canConvert = !!selectedBrand?.id && !submitting;

    const convert = async () => {
        if (!canConvert) return;
        setSubmitting(true);
        try {
            const { data } = await axiosInstant.post(salesProposalConvertEndpoint(proposal.id), {
                brand_id: selectedBrand.id,
            });
            toast.success("Proposal converted to a live order");
            onConverted(data.id);
        } catch (err: any) {
            const body = err?.response?.data;
            toast.error(
                body?.error === "UnknownBrand"
                    ? "That brand could not be found."
                    : "Failed to convert the proposal. Please try again.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <IconButton
                onClick={onClose}
                sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
                aria-label="Close"
            >
                <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: "var(--text)", mb: 0.5 }}>
                    Convert to live order
                </Typography>
                <Typography sx={{ fontSize: 13.5, color: "var(--text-2)", mb: 3, lineHeight: 1.6 }}>
                    Attach the brand to confirm this proposal. All {proposal.projects.length} draft
                    projects get the brand applied, the order moves to the regular orders list, and
                    the proposal locks for editing. Products can be assigned to the projects later.
                </Typography>

                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", mb: 0.75 }}>
                    Brand
                    {leadBrandName && !selectedBrand ? (
                        <span style={{ fontWeight: 500, color: "var(--text-2)" }}>
                            {" "}
                            — lead entered “{leadBrandName}”
                        </span>
                    ) : null}
                </Typography>
                <InlineBrandSelector
                    setFieldValue={noopSetFieldValue}
                    selectedBrand={selectedBrand}
                    setSelectedBrand={setSelectedBrand}
                    allowAdd
                />

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 28 }}>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-[13.5px] font-semibold text-text-2 border border-border-token rounded-[10px] px-5 py-2.5 bg-transparent cursor-pointer"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={!canConvert}
                        onClick={convert}
                        className={`text-[13.5px] font-semibold text-white rounded-[10px] px-5 py-2.5 border-0 ${
                            canConvert ? "bg-accent cursor-pointer" : "bg-[#CFCAC2] cursor-default"
                        }`}
                    >
                        {submitting ? "Converting…" : "Convert to order →"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
